import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ErroService } from 'src/app/service/erros/erro.service';
import { Crypto } from 'src/app/service/classes/crypto/crypto';
import { LocalStorage } from 'src/app/service/classes/localstorage/LocalStorage';
import { FolhaPagamentoService } from './folha-pagamento.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-folha-pagamento',
  templateUrl: './folha-pagamento.component.html',
  styleUrls: ['./folha-pagamento.component.css'],
})
export class FolhaPagamentoComponent implements OnInit {
  form: FormGroup;
  competencias: CompetenciaDTO[] = [];
  tiposFolha: TipoFolhaDTO[] = [];
  lotacoes: LotacaoDTO[] = [];
  vinculos: VinculoDTO[] = [];
  loading = false;

  localStorage: LocalStorage;

  constructor(
    public fb: FormBuilder,
    private spinner: NgxSpinnerService,
    private erroService: ErroService,
    private folhaService: FolhaPagamentoService,
    private router: Router
  ) {
    this.localStorage = this.decryptLocalStorage();
    this.form = this.fb.group({
      competencia: ['', Validators.required],
      tipoFolha: [{ value: '', disabled: true }, Validators.required],
      lotacao: [{ value: '', disabled: true }],
      vinculos: this.fb.array([]),
    });
    this.verificarAutenticacao();
  }

  ngOnInit(): void {
    this.carregarCompetencias();

    this.form.get('competencia')!.valueChanges.subscribe((dtcomp: string) => {
      this.form.patchValue({ tipoFolha: '', lotacao: '' }, { emitEvent: false });
      this.tiposFolha = [];
      this.lotacoes = [];
      this.clearVinculos();

      if (dtcomp) {
        this.form.get('tipoFolha')!.enable({ emitEvent: false });
        this.carregarTiposFolha(dtcomp);
      } else {
        this.form.get('tipoFolha')!.disable({ emitEvent: false });
        this.form.get('lotacao')!.disable({ emitEvent: false });
      }
    });

    this.form.get('tipoFolha')!.valueChanges.subscribe((tipo: string) => {
      this.form.patchValue({ lotacao: '' }, { emitEvent: false });
      this.lotacoes = [];
      this.clearVinculos();

      const dtcomp = this.form.get('competencia')!.value;
      if (dtcomp && tipo) {
        this.form.get('lotacao')!.enable({ emitEvent: false });
        this.carregarLotacoes(dtcomp, tipo);
        this.carregarVinculos(dtcomp, tipo);
      } else {
        this.form.get('lotacao')!.disable({ emitEvent: false });
      }
    });

    this.form.get('lotacao')!.valueChanges.subscribe((lotacao: string) => {
      const dtcomp = this.form.get('competencia')!.value;
      const tipo = this.form.get('tipoFolha')!.value;
      if (dtcomp && tipo) this.carregarVinculos(dtcomp, tipo, lotacao || undefined);
      else this.clearVinculos();
    });
  }

  get vinculosFA(): FormArray {
    return this.form.get('vinculos') as FormArray;
  }

  private obterCredencialBase64(): string {
    try {
      const utilCriptografado = localStorage.getItem('util');
      if (!utilCriptografado) return '';
      const utilDecriptado = new Crypto().decryptUsingAES256(utilCriptografado);
      const localStorageObj = JSON.parse(utilDecriptado);
      return localStorageObj?.CredencialBase64 ?? '';
    } catch {
      return '';
    }
  }

  private verificarAutenticacao(): void {
    const credencialBase64 = this.obterCredencialBase64();
    if (!credencialBase64) {
      // Redireciona para login se não estiver autenticado
      this.router.navigate(['/login']);
      return;
    }
  }

  carregarCompetencias(): void {
    this.loading = true;
    this.spinner.show('folha');

    const sigla = this.getSigla();
    const empfil = this.getEmpfil();

    this.folhaService.listarCompetencias(sigla, empfil).subscribe({
      next: (data: any) => {
        this.competencias = Array.isArray(data?.result) ? data.result : [];
        this.loading = false;
        this.spinner.hide('folha');
      },
      error: (err) => {
        this.erroService.retornoErro(err);
        this.loading = false;
        this.spinner.hide('folha');
      },
    });
  }

  carregarTiposFolha(dtcomp: string): void {
    this.spinner.show('tipos');
    const sigla = this.getSigla();
    const empfil = this.getEmpfil();

    this.folhaService.listarTiposFolha(sigla, dtcomp, empfil).subscribe({
      next: (data: any) => {
        this.tiposFolha = Array.isArray(data?.result) ? data.result : [];
        this.spinner.hide('tipos');
      },
      error: (err) => {
        this.erroService.retornoErro(err);
        this.spinner.hide('tipos');
      },
    });
  }

  carregarLotacoes(dtcomp: string, tipofolha: string): void {
    this.spinner.show('lotacoes');
    const sigla = this.getSigla();
    const empfil = this.getEmpfil();

    this.folhaService.listarLotacoes(sigla, dtcomp, empfil, tipofolha).subscribe({
      next: (data: any) => {
        this.lotacoes = Array.isArray(data?.result) ? data.result : [];

        this.spinner.hide('lotacoes');
      },
      error: (err) => {
        this.erroService.retornoErro(err);
        this.spinner.hide('lotacoes');
      },
    });
  }

  carregarVinculos(dtcomp: string, tipofolha: string, lotacao?: string): void {
    this.spinner.show('vinculos');
    const sigla = this.getSigla();
    const empfil = this.getEmpfil();

    const obs$ = lotacao
      ? this.folhaService.listarVinculosComLotacao(sigla, dtcomp, empfil, tipofolha, lotacao)
      : this.folhaService.listarVinculos(sigla, dtcomp, empfil, tipofolha);

    obs$.subscribe({
      next: (data: any) => {
        const vinculosData = Array.isArray(data?.result) ? data.result : [];

        this.vinculos = vinculosData;
        this.rebuildVinculosFormArray(this.vinculos.length);
        this.spinner.hide('vinculos');
      },
      error: (err) => {
        this.erroService.retornoErro(err);
        this.clearVinculos();
        this.spinner.hide('vinculos');
      },
    });
  }

  gerarRelatorio(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const vinculosArray = this.getVinculosSelecionados();

    const payload: any = {
      sigla: this.getSigla(),
      empfil: this.getEmpfil(),
      competencia: this.form.value.competencia,
      tipoFolha: this.form.value.tipoFolha,
      lotacao: this.form.value.lotacao || null,
      vinculosSelecionados: vinculosArray,
    };

    this.loading = true;
    this.spinner.show('relatorio');

    this.folhaService.gerarRelatorio(payload).subscribe({
      next: (res: any) => {
        const base64Data = res?.result?.file;

        if (!base64Data) {
          this.erroService.retornoErro('Base64 do PDF não encontrado na resposta');
          this.loading = false;
          this.spinner.hide('relatorio');
          return;
        }

        const nomeArquivo = `relatorio_${payload.competencia}_${payload.tipoFolha}.pdf`;
        this.downloadBase64Pdf(base64Data, nomeArquivo);

        this.loading = false;
        this.spinner.hide('relatorio');
      },
      error: (err) => {
        this.erroService.retornoErro(err);
        this.loading = false;
        this.spinner.hide('relatorio');
      },
    });
  }
  rebuildVinculosFormArray(qtd: number): void {
    const controls = Array.from({ length: qtd }, () => this.fb.control(false));
    this.form.setControl('vinculos', this.fb.array(controls));
  }

  getVinculosSelecionados(): Array<number | string> {
    const selecionados: Array<number | string> = [];
    const checks = this.vinculosFA.value as boolean[];

    checks.forEach((checked, i) => {
      if (checked && this.vinculos[i]) {
        selecionados.push(this.vinculos[i].VALOR);
      }
    });

    return selecionados;
  }

  clearVinculos(): void {
    this.vinculos = [];
    this.rebuildVinculosFormArray(0);
  }

  resetAposCompetencia(): void {
    this.form.patchValue(
      {
        tipoFolha: '',
        lotacao: '',
      },
      { emitEvent: false }
    );

    this.tiposFolha = [];
    this.lotacoes = [];
    this.clearVinculos();
  }

  resetAposTipoFolha(): void {
    this.form.patchValue(
      {
        lotacao: '',
      },
      { emitEvent: false }
    );

    this.lotacoes = [];
    this.clearVinculos();
  }

  decryptLocalStorage(): LocalStorage {
    if (localStorage.getItem('util') != null) {
      return JSON.parse(new Crypto().decryptUsingAES256(localStorage.getItem('util')));
    }
    return null;
  }

  getSigla(): string {
    return this.localStorage?.Sigla ?? '';
  }

  getEmpfil(): string {
    return String(this.localStorage?.FilialAtual ?? '');
  }

  downloadBase64Pdf(base64: string, fileName: string): void {
    const url = `data:application/pdf;base64,${base64}`;
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'relatorio.pdf';
    a.click();
  }

  trackByCodigo = (_: number, item: { codigo?: string | number; id?: string | number }) =>
    item?.codigo ?? item?.id ?? _;
}

export interface CompetenciaDTO {
  VALOR: string;
  DESCRICAO: string;
}
export interface TipoFolhaDTO {
  VALOR: string;
  DESCICAO: string;
}
export interface LotacaoDTO {
  VALOR: string;
  DESCRICAO: string;
}
export interface VinculoDTO {
  VALOR: number | string;
  DESCRICAO: string;
}

export interface RelatorioResposta {
  Base64?: string;
  file?: string;
  type?: string;
  size?: number;
  NomeArquivo?: string;
  result?: {
    file: string;
    type: string;
    size: number;
  };
}
